/* eslint-disable react/display-name */
/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react';
import Badge from './badge';
import Card from './card';
import CodeBlock from './codeBlock';
import DownloadCard from './fileDownloadCard';
import Highlights from './highlights';
import Icon from './icon';
import ImageCard from './imageCard';
import Jargon from './jargon';
import Layout from './layout';
import LinkCard from './linkCard';
import CliCommandsApiReference from './CliCommandsApiReference';
import SdkMethodsApiReference from './SdkMethodsApiReference';
import PropertiesTable from './PropertiesTable';
import PropDescription from './PropDescription';
import { pre, table, list } from '../../styles';
import { createElement } from 'react';
import { Divider } from './Divider';
import { colors, fancyBorder } from '../../theme';
import { buildElementId } from '../../utils/misc';
import { Link as GatsbyLink } from 'gatsby';
import isAbsoluteUrl from 'is-absolute-url';
import ExternalLink from '../../../static/icons/ExternalLink';
import { PreviousNext } from '../PreviousNext/PreviousNext';
import { ensureTrailingSlash } from '../../../scripts/utils/misc';

const getNameFromProps = (props) => {
  let name = props.children;
  if (Array.isArray(name)) {
    [name] = props.children;
  }
  return name;
};

const getId = (props) => {
  return buildElementId(getNameFromProps(props));
};

const Header = (level, props) => {
  return createElement(
    `h${level}`,
    {
      className: `heading${level}`,
      id: getId(props),
      ...props
    },
    createElement('span', props)
  );
};

const Table = ({ ...props }) => (
  <div css={{ display: 'grid' }}>
    <div css={{ overflowX: 'auto' }}>
      <table css={{ ...table(useTheme()), ...fancyBorder }} {...props} />
    </div>
  </div>
);

const Section = (props) => {
  let header = null;
  if (Array.isArray(props.children)) {
    header = props.children[0].props;
  } else {
    header = props.children.props;
  }
  const name = getId(header);
  return <section id={name} {...props} />;
};

const emphasis = (props) => {
  const useJargon = !(typeof props.children === 'string');
  if (useJargon) {
    return <Jargon {...props} />;
  }
  return <em {...props} />;
};

export const Link = ({ children, href }: { children: any; href: string }) => {
  const css = {
    transition: 'color 0.15s',
    color: colors.primary,
    whiteSpace: 'nowrap' as any,
    '&:hover': {
      color: colors.primaryDark
    }
  };
  return isAbsoluteUrl(href) ? (
    <a css={css} href={href} target="_blank" rel="noopener noreferrer">
      <span>
        {children}
        <span css={{ padding: '0px 4px' }}>
          <ExternalLink />
        </span>
      </span>
    </a>
  ) : (
    <GatsbyLink css={css} to={ensureTrailingSlash(href)}>
      {children}
    </GatsbyLink>
  );
};

const Description = (props) => {
  return (
    <div
      css={{
        p: { color: '#9b9999', textAlign: 'center', fontSize: '14px' },
        marginBottom: '30px',
        marginTop: '-3px'
      }}
    >
      {props.children}
    </div>
  );
};

export default {
  h1: (props) => Header(1, props),
  h2: (props) => Header(2, props),
  h3: (props) => Header(3, props),
  h4: (props) => Header(4, props),
  h5: (props) => Header(5, props),
  h6: (props) => Header(6, props),
  // FirstH1: (props) => (
  //   <h1 className="heading1" id={getId(props)} css={{ marginTop: '25px' }}>
  //     {props.children}
  //   </h1>
  // ),
  // FirstH2: (props) => (
  //   <h2 className="heading1" id={getId(props)} css={{ marginTop: '15px' }}>
  //     {props.children}
  //   </h2>
  // ),
  section: (props) => Section(props),
  blockquote: Description,
  p: (props) => <p className="paragraph" {...props} />,
  pre: (props) => <pre css={pre} {...props} />,
  table: (props) => <Table {...props} />,
  em: emphasis,
  img: ({ src, alt, style, ...rest }) => {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img loading="lazy" src={src} alt={alt} style={{ ...style }} {...rest} />
      </a>
    );
  },
  code: CodeBlock,
  ul: (props) => <ul css={list} {...props} />,
  ol: (props) => <ol css={list} {...props} />,
  a: Link,
  Badge,
  Layout,
  Icon,
  Card,
  LinkCard,
  Link,
  ImageCard,
  DownloadCard,
  ...Highlights,
  CliCommandsApiReference,
  SdkMethodsApiReference,
  PropertiesTable,
  PropDescription,
  Divider,
  WarnIcon: (props) => <Icon name="alert-circle" size={props.size || '22'} color={colors.warnColor} />,
  PreviousNext
};

// <hr
//   css={{
//     marginTop: '35px !important',
//     marginBottom: '35px !important',
//     borderColor: 'red',
//     backgroundColor: 'red',
//     height: '2px'
//   }}
// />
